import React from "react";
import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import Button from "../ui/Button";
import { useTruncatedHash } from "../../hooks/useTruncatedHash";
import Modal from "../ui/Modal";
import StudyController from "@/network/controllers/study.controller";
import ParticipantController from "@/network/controllers/participant.controller";
import { sha256 } from "@/utils/crypto";
import { useSession } from "@/hooks/useSession";
import State, { STATE, CurrentStudyState } from "@state";
import { getSession } from "@/hooks/useSession";
import AuthController from "@/network/controllers/auth.controller";
import EmailController from "@/network/controllers/email.controller";

const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return "No data available";
  const date = new Date(dateString);
  const options = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  if (includeTime) {
    options.hour = "numeric";
    options.minute = "numeric";
    options.hour12 = true;
  }
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

const getStudyFromState = () => STATE?.[CurrentStudyState.STUDY] ?? null;

const deriveDisplayStatus = (patient) => {
  const patientStatus = patient.patient_status?.toLowerCase() || null;
  const investigatorStatus = patient.investigator_status?.toLowerCase() || null;

  // 🟥 Le patient n'a pas encore donné son consentement
  if (!patientStatus || patientStatus === "pending") {
    return "(re)consent required";
  }

  // 🟧 Le patient a signé mais l'investigateur n'a pas encore contre-signé
  if (patientStatus === "validated" && investigatorStatus !== "validated") {
    return "Signature required";
  }

  // 🟩 Le patient et l'investigateur ont tous les deux validé le consentement
  if (patientStatus === "validated" && investigatorStatus === "validated") {
    return "consented";
  }

  // ⚫️ Le patient a retiré son consentement
  if (patientStatus === "revoked") {
    return "withdrawn";
  }

  // ⚫️ Le patient a refusé de participer à l'étude
  if (patientStatus === "refused") {
    return "dropped_out";
  }

  // Par défaut : retourne le statut normalisé
  return patientStatus.charAt(0).toUpperCase() + patientStatus.slice(1);
};

const getStatusTextColor = (status) => {
  switch (status?.toLowerCase()) {
    case "consented":
      return "";
    case "(re)consent required":
      return "";
    case "signature required":
      return "";
    case "withdrawn":
    case "dropped_out":
      return "";
    default:
      return "";
  }
};

const getStatusDisplayText = (status) => {
  switch (status?.toLowerCase()) {
    case "signature required":
      return "Counter-signature required";
    case "(re)consent required":
      return "(Re)consent required";
    case "consented":
      return "Consented";
    case "dropped_out":
      return "Dropped-out";
    case "withdrawn":
      return "Withdrawn";
    default:
      return status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : "Unknown";
  }
};

// Helper function to generate the consent message
const getConsentMessage = (patient) => {
  const patientSignedDate = formatDate(patient.latestAction);
  const hasPatientSignature =
    patient.icfSignature && patient.icfSignature !== "null";
  const hasInvestigatorSignature =
    patient.icfCounterSignature && patient.icfCounterSignature !== "null";

  switch (patient.status?.toLowerCase()) {
    case "consented":
      if (hasPatientSignature && hasInvestigatorSignature) {
        return `The patient provided consent for the site ICF on ${patientSignedDate}, and the investigator has countersigned. The patient has been included in the clinical study since then.`;
      }

      return `The patient's consent status is ${patient.status}.`;
    case "signature required":
      return `The patient provided consent for the site ICF on ${patientSignedDate}, but the investigator has not yet countersigned. Until the investigator signs, the patient's participation is not fully validated. Please proceed with the countersignature.`;
    case "(re)consent required":
      return "A (re)consent is required to the site ICF. The patient must sign the version to continue participating. Without this signature, the patient cannot proceed further in the study.";
    case "withdrawn":
      return `The patient withdrew their consent on ${patientSignedDate}. From this date onward, the patient is no longer participating in the study, and no further study-related procedures can be performed.`;
    case "dropped_out":
      return `The patient has dropped out of the study.${
        patient.comments ? ` Investigator's comment: ${patient.comments}` : ""
      }`;
    default:
      return "";
  }
};

export default function PatientsManagementSection({ studyId }) {
  const { user } = useSession();
  const [currentStudy, setCurrentStudy] = useState(() => getStudyFromState());
  useEffect(() => {
    setCurrentStudy(getStudyFromState());
    if (typeof State?.subscribe === "function") {
      const unsubscribe = State.subscribe(CurrentStudyState.STUDY, (val) => {
        setCurrentStudy(val);
      });
      return unsubscribe;
    }
  }, []);

  const effectiveStudyId = studyId || currentStudy?.id || null;
  const currentInvestigatorId = currentStudy?.investigator_id ?? null;
  const [patientsData, setPatientsData] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [errorPatients, setErrorPatients] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedPatientId, setExpandedPatientId] = useState(null);
  const { truncate } = useTruncatedHash();

  const isClosed = String(currentStudy?.status || "").toLowerCase() === "closed";
  const [showDropOutModal, setShowDropOutModal] = useState(false);
  const [patientToDropOut, setPatientToDropOut] = useState(null);
  const [dropOutReason, setDropOutReason] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // pour les modals
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [signatureType, setSignatureType] = useState("");
  const [showSignatureRequiredModal, setShowSignatureRequiredModal] =
    useState(false);
  const [patientForSignature, setPatientForSignature] = useState(null);
  const [
    showInvestigatorCounterSignatureModal,
    setShowInvestigatorCounterSignatureModal,
  ] = useState(false);
  const [showReconsentModal, setShowReconsentModal] = useState(false);
  const [patientToReconsent, setPatientToReconsent] = useState(null);
  const [investigatorFile, setInvestigatorFile] = useState(null);
  const [isUploadingCounter, setIsUploadingCounter] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  //donnes pour le modal contact information
  const [showContactEditModal, setShowContactEditModal] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [editedContactInfo, setEditedContactInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    number: "",
    city: "",
    postalCode: "",
    country: "",
  });

  const handleInvestigatorFileChange = (e) => {
    if (e.target.files && e.target.files[0])
      setInvestigatorFile(e.target.files[0]);
  };

  // -----------------------------------------------
  // TODO: CALL BLOCKCHAIN
  // -----------------------------------------------

  const handleUploadCounterSignature = async () => {
    if (!patientForSignature || !investigatorFile) {
      alert("Missing file or patient selection");
      return;
    }
    if (!currentInvestigatorId) {
      alert(
        "Missing investigator ID from state. Please reopen the page or try again."
      );
      return;
    }
    try {
      setIsUploadingCounter(true);

      console.log(
        "=== patientForSignature === PatientManagement.jsx === key: 593204 ==="
      );
      console.dir(patientForSignature, { depth: null, colors: true });
      console.log("=================================");

      console.log("=== 🧪 DEBUG: Verifica consent_id inviato ===");
      console.log("Consent ID:", patientForSignature?.consent_id);
      console.log("Participant ID:", patientForSignature?.id);
      console.log("Site ICF ID:", patientForSignature?.site_icf_id);
      console.log("Study ID:", currentStudy?.id);
      console.log("File to upload:", investigatorFile);
      console.log("=================================");

      const sendConsentToBC =
        await ParticipantController.uploadInvestigatorConfirmConsent({
          participant_id: patientForSignature.patient_id,
          site_icf_id: patientForSignature.site_icf_id,
          consent_id: patientForSignature.consent_id,
          study_id: currentStudy?.id,
          participant_file: investigatorFile,
        });

      console.log(
        "=== sendConsentToBC === PatientManagement.jsx === key: 952603 ==="
      );
      console.dir(sendConsentToBC, { depth: null, colors: true });
      console.log("=================================");

      // return;

      const params = {
        studyId: currentStudy?.id,
        participant_id: patientForSignature.patient_id,
        investigator_id: currentInvestigatorId,
        site_icf_id: patientForSignature.site_icf_id,
        investigator_file: investigatorFile,
        investigator_file_hash: "",
        investigator_signature: "",
      };

      params.investigator_signature = sendConsentToBC.signature;
      params.investigator_file_hash = sendConsentToBC.encryptedFile;

      console.log("→ Upload counter-sign params:", params);

      await new Promise((r) => setTimeout(r, 1500));

      const res = await ParticipantController.saveInvestigatorConfirmConsent({
        ...params,
      });

      if (res?.success) {
        await reloadPatients();
        setShowInvestigatorCounterSignatureModal(false);
        setInvestigatorFile(null);
        alert("Counter-signature uploaded successfully.");
      } else {
        console.error("Counter-sign upload failed:", res?.message);
        alert(res?.message || "Upload failed");
      }
    } catch (error) {
      console.log("=== error === PatientManagement.jsx === key: 777534 ===");
      console.dir(error, { depth: null, colors: true });
      console.log("=================================");
      alert("Unexpected error during upload.");
    } finally {
      setIsUploadingCounter(false);
    }
  };

  const handleSendByEmail = async (patient) => {
    if (!patient) {
      console.error("❌ Patient not provided");
      return;
    }

    const email = patient.email_address;
    const firstname = patient.patient_first_name;
    const lastname = patient.patient_last_name;
    const participant_id = patient.id;
    const site_icf_id = patient?.site_icf_id;
    const consent_id = patient?.consent_id;

    console.log("📨 Attempting email send with:", {
      email,
      firstname,
      lastname,
      participant_id,
      site_icf_id,
      consent_id,
    });

    if (
      !email ||
      !firstname ||
      !lastname ||
      !participant_id ||
      !site_icf_id ||
      !consent_id
    ) {
      alert("❌ Missing required patient data");
      return;
    }

    try {
      const result = await EmailController.resendICF({
        email,
        firstname,
        lastname,
        participant_id,
        site_icf_id,
        consent_id,
      });

      if (result?.success === false) {
        console.error("❌ Failed to send email:", result.message);
        alert("Failed to send email.");
      } else {
        console.log("✅ Email sent successfully");
        alert("Email sent successfully.");
      }
    } catch (err) {
      console.error("Unexpected error while sending email:", err);
      alert("An unexpected error occurred.");
    }
  };

  useEffect(() => {
    console.log("[PatientsManagement] derived ids →", {
      effectiveStudyId,
      currentInvestigatorId,
      studyIdProp: studyId,
      currentStudyFromState: currentStudy?.id,
      investigatorFromState: currentStudy?.investigator_id,
    });

    const fetchPatients = async () => {
      if (!effectiveStudyId) {
        setErrorPatients("Study ID not available for patients.");
        setLoadingPatients(false);
        return;
      }
      setLoadingPatients(true);
      setErrorPatients(null);
      try {
        const response =
          await StudyController.getInvestigatorPatientsManagement(
            effectiveStudyId
          );
        console.log("API response for patients management:", response);
        if (response.success) {
          if (Array.isArray(response.data.data)) {
            const mappedPatients = (response.data.data || []).map((patient) => {
              const addressParts = [
                patient.office_address_street,
                patient.office_address_number,
                patient.office_address_city,
                patient.office_address_postal_code,
                patient.office_address_country,
              ].filter(Boolean);
              const fullAddress =
                addressParts.length > 0 ? addressParts.join(", ") : "N/A";
              const displayStatus = deriveDisplayStatus(patient);

              const patientObject = {
                id: patient.patient_id,
                comments: patient.comments || "",
                consentMessage: "",
                patient_id: patient.patient_id,
                consent_id: patient.consent_id,
                site_icf_id: patient.site_icf_id,
                name: `${patient.patient_first_name || ""} ${
                  patient.patient_last_name || ""
                }`.trim(),
                status: displayStatus,
                masterICFVersion: patient.master_icf_title || "N/A",
                milestone: patient.latest_milestone_title || "N/A",
                latestAction: patient.last_action_timestamp,
                contactInfo: {
                  email: patient.email_address || "N/A",
                  phone: patient.contact_number || "N/A",
                  address: fullAddress,
                },
                icfSignature: patient.icf_signature || "null",
                icfCounterSignature: patient.icf_counter_signature || "null",
                consentHistory: patient.patient_consent_history || [],

                patient_first_name: patient.patient_first_name || "",
                patient_last_name: patient.patient_last_name || "",
                email_address: patient.email_address || "",
                contact_number: patient.contact_number || "",
                office_address_street: patient.office_address_street || "",
                office_address_number: patient.office_address_number || "",
                office_address_city: patient.office_address_city || "",
                office_address_postal_code:
                  patient.office_address_postal_code || "",
                office_address_country: patient.office_address_country || "",
              };
              patientObject.consentMessage = getConsentMessage(patientObject);
              return patientObject;
            });
            setPatientsData(mappedPatients);
            setErrorPatients(null);
          } else {
            setErrorPatients("Invalid data format received from server.");
            console.error("Invalid data format:", response.data.data);
          }
        } else {
          setErrorPatients(
            response.message || "Failed to retrieve patient data."
          );
          console.error(
            "Failed to retrieve patient data:",
            response.message || "No message provided."
          );
        }
      } catch (err) {
        // Errore di rete o eccezione non gestita nel blocco try
        setErrorPatients(
          "An unexpected error occurred while loading patient data."
        );
        console.error("Unexpected error during patient data fetch:", err);
      } finally {
        setLoadingPatients(false);
      }
    };
    fetchPatients();
  }, [studyId, effectiveStudyId, currentInvestigatorId, currentStudy]);

  const filteredPatients = patientsData.filter(
    (patient) =>
      searchQuery === "" ||
      patient.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      patient.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      patient.status?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleConsultationClick = (signature, type) => {
    setSelectedSignature(signature);
    setSignatureType(type);
    setShowConsultationModal(true);
  };

  const handleModifyClick = (patient, signature, type) => {
    setSelectedPatient(patient);
    setSelectedSignature(signature);
    setSignatureType(type);
    setShowModifyModal(true);
  };

  const handleEditContactClick = (patient) => {
    const contactInfo = patient.contactInfo || {};
    const [firstName, ...restLast] = (patient.name || "").split(" ");
    const lastName = restLast.join(" ");

    const raw = (contactInfo.address || "").trim();

    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Valori di default
    let street = "";
    let number = "";
    let city = "";
    let postalCode = "";
    let country = "";

    if (parts.length > 0) street = parts[0] || "";

    if (parts.length > 1) {
      if (/^\d+[A-Za-z]?$/.test(parts[1])) {
        number = parts[1] || "";
        city = parts[2] || "";
        postalCode = parts[3] || "";
        country = parts[4] || "";
      } else {
        const m = (parts[0] || "").match(/(.+?)\s+(\d+[A-Za-z]?)$/);
        if (m) {
          street = m[1].trim();
          number = m[2].trim();
        }
        city = parts[1] || "";
        postalCode = parts[2] || "";
        country = parts[3] || "";
      }
    }

    setPatientToEdit(patient);
    setEditedContactInfo({
      firstName: firstName || "",
      lastName: lastName || "",
      email: contactInfo.email || "",
      phone: contactInfo.phone || "",
      street,
      number,
      city,
      postalCode,
      country,
    });
    setShowContactEditModal(true);
  };

  const handleUpdateContact = async () => {
    if (!patientToEdit) return;

    const addressStr = [editedContactInfo.street, editedContactInfo.number]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(", ");

    const updates = {
      firstname: (editedContactInfo.firstName ?? "").trim() || null,
      lastname: (editedContactInfo.lastName ?? "").trim() || null,
      email: (editedContactInfo.email ?? "").trim() || null,
      phone_number: (editedContactInfo.phone ?? "").trim() || null,
      postal_address_1: addressStr || null,
      postal_address_2: addressStr || null,
      country: (editedContactInfo.country ?? "").trim() || null,
      city: (editedContactInfo.city ?? "").trim() || null,
      zipcode: (editedContactInfo.postalCode ?? "").trim() || null,
    };

    try {
      const res = await ParticipantController.updateParticipantUserInfo({
        studyId: effectiveStudyId,
        participantId: patientToEdit.id,
        updates,
      });

      if (!res?.success) {
        console.error("updateParticipantUserInfo failed:", res);
        alert(res?.message || "Update failed.");
        return;
      }

      const parts = String(res?.data?.postal_address_1 ?? "")
        .split(",")
        .map((s) => s.trim());

      setPatientsData((prev) =>
        prev.map((p) => {
          if (p.id !== patientToEdit.id) return p;
          const next = {
            ...p,
            name: `${
              (res?.data?.firstname ?? editedContactInfo.firstName) || ""
            } ${
              (res?.data?.lastname ?? editedContactInfo.lastName) || ""
            }`.trim(),
            contactInfo: {
              email: res?.data?.email ?? p.contactInfo.email,
              phone: res?.data?.phone_number ?? p.contactInfo.phone,
              address: [
                parts[0],
                parts[1],
                res?.data?.city,
                res?.data?.zipcode,
                res?.data?.country,
              ]
                .filter(Boolean)
                .join(", "),
            },
          };
          return next;
        })
      );

      setShowContactEditModal(false);
      setPatientToEdit(null);
      alert("Patient information updated successfully!");
    } catch (e) {
      console.error("Unexpected error during updateParticipantUserInfo:", e);
      alert("Unexpected error during update.");
    }
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setEditedContactInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Déterminer le style de la ligne en fonction du statut du patient
  const getRowStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "signature required": // Style pour les patients nécessitant une contre-signature - ombre intérieure orange
        return "border border-orange-500 shadow-[inset_0_0_12px_rgba(255,125,44,1)] relative z-10";
      case "(re)consent required": // Style pour les patients nécessitant un nouveau consentement - ombre intérieure rouge
        return "border border-red-600 shadow-[inset_0_0_12px_rgba(209,26,42,1)] relative z-10";
      case "withdrawn":
      case "dropped_out": // Style pour les patients retirés ou ayant abandonné - testo grigio, bordo e ombra grigi
        return "border border-gray-300 shadow-[inset_0_0_12px_rgba(128,128,128,0.5)] relative z-10 text-gray-400";
      default:
        return "";
    }
  };

  // Obtenir la couleur de l'icône et du bord en fonction du statut
  const getStatusIconColor = (status) => {
    switch (
      status?.toLowerCase() // Converti lo stato in minuscolo per il confronto
    ) {
      case "signature required":
        return {
          iconFilter:
            "invert(65%) sepia(54%) saturate(2651%) hue-rotate(346deg) brightness(97%) contrast(96%)",
          borderColor: "border-orange-500",
          bgColor: "bg-orange-100",
        };
      case "(re)consent required":
        return {
          iconFilter:
            "invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(96%)",
          borderColor: "border-red-600",
          bgColor: "bg-red-100",
        };
      case "withdrawn":
      case "dropped_out":
        return {
          iconFilter:
            "invert(70%) sepia(0%) saturate(0%) hue-rotate(153deg) brightness(90%) contrast(90%)",
          borderColor: "border-gray-300",
          bgColor: "bg-gray-200",
        };
      default:
        return {
          iconFilter: "none",
          borderColor: "border-blue-500",
          bgColor: "bg-blue-100",
        };
    }
  };

  const handleCopyLinkClick = async (patient) => {
    try {
      console.log("📦 Patient data:", patient);
      console.log("🔍 Participant ID:", patient.patient_id);
      console.log("🔍 Study ID (from state):", effectiveStudyId);

      const res = await ParticipantController.getPatientConsentDocument({
        participant_id: patient.patient_id,
        study_id: effectiveStudyId,
      });

      console.log("📄 Response from getPatientConsentDocument:", res);

      if (res?.success && res?.blob) {
        const url = URL.createObjectURL(res.blob);
        console.log("🔗 Blob URL generated:", url);
        window.open(url, "_blank");
      } else {
        console.warn("⚠️ Consent document not found.");
        alert("Consent document not found.");
      }
    } catch (error) {
      console.error("❌ Error fetching consent document:", error);
      alert("An error occurred while retrieving the consent document.");
    }
  };

  const openICFForCounterSign = async () => {
    try {
      if (!patientForSignature?.patient_id || !effectiveStudyId) {
        alert("Missing study or patient identifiers.");
        return;
      }
      const res = await ParticipantController.getPatientConsentDocument({
        participant_id: patientForSignature.patient_id,
        study_id: effectiveStudyId,
      });
      if (res?.success && res?.blob) {
        const url = URL.createObjectURL(res.blob);
        window.open(url, "_blank");
      } else {
        console.warn("Consent document not found.");
        alert("Consent document not found.");
      }
    } catch (error) {
      console.error("Error fetching consent document:", error);
      alert("An error occurred while retrieving the consent document.");
    }
  };

  const reloadPatients = async () => {
    if (!effectiveStudyId) return;
    try {
      const response = await StudyController.getInvestigatorPatientsManagement(
        effectiveStudyId
      );
      if (response.success && Array.isArray(response.data.data)) {
        const mapped = response.data.data.map((patient) => {
          const addressParts = [
            patient.office_address_street,
            patient.office_address_number,
            patient.office_address_city,
            patient.office_address_postal_code,
            patient.office_address_country,
          ].filter(Boolean);
          const fullAddress = addressParts.length
            ? addressParts.join(", ")
            : "N/A";
          const displayStatus = deriveDisplayStatus(patient);

          const p = {
            id: patient.patient_id,
            patient_id: patient.patient_id,
            site_icf_id: patient.site_icf_id,
            name: `${patient.patient_first_name || ""} ${
              patient.patient_last_name || ""
            }`.trim(),
            status: displayStatus,
            masterICFVersion: patient.master_icf_title || "N/A",
            milestone: patient.latest_milestone_title || "N/A",
            latestAction: patient.last_action_timestamp,
            contactInfo: {
              email: patient.email_address || "N/A",
              phone: patient.contact_number || "N/A",
              address: fullAddress,
            },
            icfSignature: patient.icf_signature || "null",
            icfCounterSignature: patient.icf_counter_signature || "null",
            consentHistory: patient.patient_consent_history || [],
            consentMessage: "",

            patient_first_name: patient.patient_first_name || "",
            patient_last_name: patient.patient_last_name || "",
            email_address: patient.email_address || "",
            contact_number: patient.contact_number || "",
            office_address_street: patient.office_address_street || "",
            office_address_number: patient.office_address_number || "",
            office_address_city: patient.office_address_city || "",
            office_address_postal_code:
              patient.office_address_postal_code || "",
            office_address_country: patient.office_address_country || "",
          };
          p.consentMessage = getConsentMessage(p);
          return p;
        });
        setPatientsData(mapped);
      }
    } catch (e) {
      console.error("reloadPatients error", e);
    }
  };

  const handleFileClick = (patientId) => {
    if (expandedPatientId === patientId) {
      setExpandedPatientId(null);
    } else {
      setExpandedPatientId(patientId);
    }
  };

  // Gestion de l'ouverture du modal de désinscription
  const handleDropOutClick = (patient) => {
    setPatientToDropOut(patient);
    setDropOutReason("");
    setShowDropOutModal(true);
  };

  // Gestion de la confirmation de désinscription
  const handleConfirmDropOut = async () => {
    if (!patientToDropOut) return;

    try {
      const response = await ParticipantController.revokePatientConsent({
        studyId: effectiveStudyId,
        consent_id: patientToDropOut.consent_id,
        site_icf_id: patientToDropOut.site_icf_id,
        participant_id: patientToDropOut.id,
        dropOut: true,
        comments: dropOutReason,
      });

      if (response.success) {
        await reloadPatients();
        alert("Patient successfully marked as dropped_out.");
      } else {
        console.error("Drop-out failed:", response.message);
        alert("Failed to drop out patient: " + response.message);
      }
    } catch (error) {
      console.error("Error during drop-out:", error);
      alert("An error occurred while processing the drop-out.");
    } finally {
      setShowDropOutModal(false);
      setPatientToDropOut(null);
      setDropOutReason("");
    }
  };

  // New function to handle requesting re-consent
  const handleRequestReconsent = async (patient) => {
    setPatientToReconsent(patient);
    setShowReconsentModal(true);
  };

  const handleConfirmReconsent = async () => {
    if (!patientToReconsent) return;
    try {
      const response = await StudyController.updateParticipantConsentStatus(
        patientToReconsent.id,
        "(re)consent required"
      );
      if (response.success) {
        setPatientsData((prevPatients) =>
          prevPatients.map((p) =>
            p.id === patientToReconsent.id
              ? {
                  ...p,
                  status: "(re)consent required",
                  consentMessage: getConsentMessage({
                    ...p,
                    status: "(re)consent required",
                  }),
                }
              : p
          )
        );
        console.log(
          "Patient status updated to (re)consent required successfully."
        );
      } else {
        console.error(
          "Failed to update patient status for re-consent:",
          response.message
        );
        alert("Failed to request re-consent: " + response.message);
      }
    } catch (error) {
      console.error(
        "Error calling updateParticipantConsentStatus API for re-consent:",
        error
      );
      alert("An error occurred while requesting re-consent.");
    } finally {
      setShowReconsentModal(false);
      setPatientToReconsent(null);
    }
  };

  // Rendu du panneau de détails du patient
  const renderPatientDetails = (patient) => {
    const { borderColor, bgColor } = getStatusIconColor(patient.status);
    return (
      <div
        className={`bg-tableau-bg1 rounded-b-lg overflow-hidden transition-all duration-500 ease-in-out border-t-0 border-2 ${borderColor} ${isClosed ? "pointer-events-none opacity-60" : ""}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Colonne 1: Informations du patient */}
          <div className="bg-tableau-bg1 p-3 rounded-lg w-full">
            <h3 className="text-md font-medium text-center mb-2">
              {patient.name}
            </h3>
            <p className="mb-2 w-full text-start">
              {" "}
              Patient ID : {truncate(patient.id)}{" "}
              <span className="ml-2 inline-block">
                <img
                  src="/icons/study/Consultation.svg"
                  alt="Copier"
                  className="w-4 h-4 inline"
                />
              </span>
            </p>
            <div className=" bg-tableau-bg mt-4 p-2 ml-2 rounded-2xl w-full">
              <div className=" flex justify-center gap-2 items-center mb-2">
                <h4 className="font-medium">Contact information</h4>
                <button
                  onClick={() => handleEditContactClick(patient)}
                  className=""
                >
                  <img
                    src="/icons/study/Modifie.svg"
                    alt="Modifier"
                    className="w-4 h-4"
                  />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <p
                    className="text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                    title={patient.contactInfo.email}
                  >
                    {" "}
                    Email: {patient.contactInfo.email}
                  </p>
                  <p className="text-sm">Phone: {patient.contactInfo.phone}</p>
                </div>
                <div>
                  <p className="text-sm">
                    Address: {patient.contactInfo.address}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="grid grid-cols-[auto_1fr_auto] gap-x-2 mb-3">
                <div className="text-sm whitespace-nowrap">ICF signature :</div>
                <div className="text-sm overflow-hidden text-ellipsis ">
                  {patient.icfSignature && patient.icfSignature !== "null" ? (
                    truncate(patient.icfSignature)
                  ) : (
                    <div className="flex justify-start">
                      <Button
                        variant="icfSignature"
                        onClick={() => {
                          setPatientForSignature(patient);
                          setShowSignatureRequiredModal(true);
                        }}
                        className="flex h-7 py-2 text-sm"
                      >
                        {" "}
                        Patient Signature required
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center">
                  {patient.icfSignature && patient.icfSignature !== "null" && (
                    <>
                      <button
                        type="button"
                        className="p-1 hover:bg-blue-100 rounded-full"
                        onClick={() =>
                          handleConsultationClick(patient.icfSignature, "icf")
                        }
                      >
                        <img
                          src="/icons/study/Consultation.svg"
                          alt="Consulter"
                          className="w-4 h-4"
                        />
                      </button>
                      <button
                        type="button"
                        className="p-1 hover:bg-blue-100 rounded-full ml-1"
                        onClick={() => handleCopyLinkClick(patient)}
                      >
                        <img
                          src="/icons/study/Link.svg"
                          alt="Lien"
                          className="w-4 h-4"
                        />
                      </button>
                      <button
                        type="button"
                        className="p-1 hover:bg-blue-100 rounded-full ml-1"
                        onClick={() =>
                          handleModifyClick(
                            patient,
                            patient.icfSignature,
                            "icf"
                          )
                        }
                      >
                        <img
                          src="/icons/study/Modifie.svg"
                          alt="Modifier"
                          className="w-4 h-4"
                        />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-[auto_1fr_auto] gap-x-2">
                <div className="text-sm whitespace-nowrap">ICF signature :</div>
                <div className="text-sm overflow-hidden text-ellipsis">
                  {patient.icfCounterSignature &&
                  patient.icfCounterSignature !== "null" ? (
                    truncate(patient.icfCounterSignature)
                  ) : (
                    <>
                      {patient.status === "Signature required" ? (
                        <Button
                          variant="icfSignature"
                          className="w-full h-7 py-2 text-xs"
                          onClick={() => {
                            setPatientForSignature(patient);
                            setShowInvestigatorCounterSignatureModal(true);
                          }}
                        >
                          {" "}
                          Investigator c-signature
                        </Button>
                      ) : (
                        <Button
                          variant="icfCounterSignature"
                          className="w-full h-7 py-2 text-xs"
                        >
                          {" "}
                          Patient signature pending
                        </Button>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center">
                  {patient.icfCounterSignature &&
                    patient.icfCounterSignature !== "null" && (
                      <>
                        <button
                          type="button"
                          className="p-1 hover:bg-blue-100 rounded-full"
                          onClick={() => {
                            handleConsultationClick(
                              patient.icfCounterSignature,
                              "counter"
                            );
                          }}
                        >
                          <img
                            src="/icons/study/Consultation.svg"
                            alt="Consulter"
                            className="w-4 h-4"
                          />
                        </button>
                        <button
                          type="button"
                          className="p-1 hover:bg-blue-100 rounded-full ml-1"
                        >
                          <img
                            src="/icons/study/Link.svg"
                            alt="Lien"
                            className="w-4 h-4"
                          />
                        </button>
                        <button
                          type="button"
                          className="p-1 hover:bg-blue-100 rounded-full ml-1"
                          onClick={() => {
                            
                              setPatientForSignature(patient);
                              setShowInvestigatorCounterSignatureModal(true);
                            }}
                        >
                          <img
                            src="/icons/study/Modifie.svg"
                            alt="Modifier"
                            className="w-4 h-4"
                          />
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
          {/* Colonnes 2-3: Message de statut e pulsanti sulla stessa riga */}
          <div className="md:col-span-2 bg-tableau-bg p-4">
            {/* Message de statut e pulsanti sulla stessa riga */}
            <div className="flex items-center justify-between gap-4 mb-3">
              <div
                className={`p-2 rounded-xl flex-grow ${
                  patient.status === "(re)consent required"
                    ? "bg-red-100"
                    : patient.status === "Signature required"
                    ? "bg-orange-100"
                    : patient.status === "withdrawn" ||
                      patient.status === "dropped_out"
                    ? "bg-gray-200"
                    : patient.status === "consented"
                    ? "bg-green-100"
                    : ""
                }`}
              >
                <p className="text-sm">{patient.consentMessage}</p>
              </div>
              {!["withdrawn", "dropped_out"].includes(
                patient.status?.toLowerCase()
              ) && (
                <div className="flex gap-2">
                  <Button
                    variant="dropOut"
                    className="w-full md:w-auto text-white px-4 py-2"
                    onClick={() => handleDropOutClick(patient)}
                  >
                    {" "}
                    Drop out
                  </Button>
                </div>
              )}
            </div>
            {/* Historique des actions du paziente  */}
            <div className="bg-tableau-bg1 mt-10 p-2 rounded-lg">
              <h4 className="font-medium text-sm mb-1">Last action</h4>
              <div className="max-h-28 overflow-y-auto pr-2">
                {patient.consentHistory.length > 0 ? (
                  patient.consentHistory.map((action, index) => (
                    <p key={index} className="text-xs mb-1 leading-tight">
                      {action.description} –{" "}
                      {formatDate(action.timestamp, true)}
                    </p>
                  ))
                ) : (
                  <p className="text-xs mb-1 leading-tight">
                    No actions recorded.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loadingPatients) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (errorPatients) {
    return (
      <div className="text-xl text-red-600 text-center py-10">
        Error loading patients: {errorPatients}
      </div>
    );
  }

  // Rendu principal del componente
  return (
    <div className="bg-dashboard-bg1 p-2 rounded-xl">
      {/* En-tête avec titre et barre de recherche */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-medium">Patients management</h2>
        <div className="relative w-96">
          <input
            type="text"
            placeholder="Browse patients: Name, ID, Status..."
            className="w-full pl-4 pr-10 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Tableau des patients */}
      <div className="bg-dashboard-bg rounded-xl overflow-hidden">
        <table className="border-collapse w-full table-fixed">
          {/* En-tête du tableau */}
          <thead>
            <tr className="text-left bg-tableau-bg">
              <th className="py-3 text-xs sm:text-sm px-2 font-medium text-gray-700 w-[15%]">
                {" "}
                Patient ID <span className="ml-1">▼</span>
              </th>
              <th className="py-3 text-xs sm:text-sm px-2 font-medium text-gray-700 w-[15%]">
                {" "}
                Patient name <span className="ml-1">▼</span>
              </th>
              <th className="py-3 text-xs sm:text-sm px-3 font-medium text-gray-700 w-[16%]">
                {" "}
                Status <span className="ml-1">▼</span>
              </th>
              <th className="py-3 text-xs sm:text-sm px-1 font-medium text-gray-700 w-[16%]">
                {" "}
                Master ICF Version <span className="ml-1">▼</span>
              </th>
              <th className="py-3 text-xs sm:text-sm text-center px-3 font-medium text-gray-700 w-[22%]">
                {" "}
                Milestone <span className="ml-1">▼</span>
              </th>
              <th className="py-3 text-xs sm:text-sm px-3 font-medium text-gray-700 w-[18%]">
                {" "}
                Latest action <span className="ml-1">▼</span>
              </th>
              <th className="py-3 text-xs sm:text-sm px-3 w-[6%]"></th>
            </tr>
          </thead>
          {/* Corps du tableau */}
          <tbody className="relative">
            {filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-6 bg-tableau-bg1">
                  {" "}
                  No patient found.
                </td>
              </tr>
            ) : (
              filteredPatients.map((patient, index) => {
                const { iconFilter } = getStatusIconColor(patient.status);
                return (
                  <React.Fragment key={patient.id}>
                    {/* Ligne du patient */}
                    <tr
                      className={`${
                        index % 2 === 0 ? "bg-tableau-bg1" : "bg-tableau-bg"
                      } ${getRowStyle(patient.status)}`}
                    >
                      <td
                        className={`py-2.5 px-3 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
                          patient.status === "Withdrawn" ||
                          patient.status === "Dropped-out"
                            ? "text-gray-400"
                            : ""
                        }`}
                      >
                        {truncate(patient.id)}
                      </td>
                      <td
                        className={`py-2.5 px-3 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
                          patient.status === "Withdrawn" ||
                          patient.status === "Dropped-out"
                            ? "text-gray-400"
                            : ""
                        }`}
                      >
                        {patient.name}
                      </td>
                      <td
                        className={`py-2.5 px-3 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
                          patient.status === "Withdrawn" ||
                          patient.status === "Dropped-out"
                            ? "text-gray-400"
                            : ""
                        }`}
                        title={patient.status}
                      >
                        <span className={getStatusTextColor(patient.status)}>
                          {getStatusDisplayText(patient.status)}
                        </span>
                      </td>
                      <td
                        className={`py-2.5 px-3 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
                          patient.status === "Withdrawn" ||
                          patient.status === "Dropped-out"
                            ? "text-gray-400"
                            : ""
                        }`}
                      >
                        {patient.masterICFVersion}
                      </td>
                      <td
                        className={`py-2.5 px-3 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
                          patient.status === "Withdrawn" ||
                          patient.status === "Dropped-out"
                            ? "text-gray-400"
                            : ""
                        }`}
                      >
                        {patient.milestone}
                      </td>
                      <td
                        className={`py-2.5 px-3 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
                          patient.status === "Withdrawn" ||
                          patient.status === "Dropped-out"
                            ? "text-gray-400"
                            : ""
                        }`}
                      >
                        {formatDate(patient.latestAction, true)}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {/* Bouton pour développer/réduire les détails du patient */}
                        <button
                          className={`p-1 rounded-full hover:bg-blue-100 transition-colors ${
                            patient.status === "Withdrawn" ||
                            patient.status === "Dropped-out"
                              ? "opacity-50"
                              : ""
                          }`}
                          onClick={() => handleFileClick(patient.id)}
                        >
                          <img
                            alt="Fichier"
                            className="w-4 h-4 transform scale-125"
                            style={{ maxWidth: "none", filter: iconFilter }}
                            src="/icons/study/File.svg"
                          />
                        </button>
                      </td>
                    </tr>
                    {/* Panneau details patient  */}
                    {expandedPatientId === patient.id && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <div className="animate-fadeIn">
                            {renderPatientDetails(patient)}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={showDropOutModal}
        onClose={() => setShowDropOutModal(false)}
        title="Patient drop-out"
        primaryAction={handleConfirmDropOut}
        primaryActionLabel="Confirm drop-out"
        primaryButtonVariant="addbutton2"
        secondaryAction={() => setShowDropOutModal(false)}
        secondaryActionLabel="Cancel"
        secondaryButtonVariant="addbutton"
        size="xl"
      >
        <p className="mb-4">
          {" "}
          You are about to mark this patient as "Dropped-out" from the clinical
          study. This action is irreversible, and the patient will no longer
          participate in any further study procedures. Please provide a
          justification for the drop-out:
        </p>
        <p className="mb-2">Please provide a justification for the drop-out:</p>
        <textarea
          value={dropOutReason}
          onChange={(e) => setDropOutReason(e.target.value)}
          placeholder="Reason for the drop-out, such as adverse events, protocol deviations, or any other relevant circumstances."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
        />
      </Modal>

      <Modal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        title=""
        primaryAction={() => setShowConsultationModal(false)}
        primaryActionLabel="Close"
        size="xl"
      >
        <div className="mb-4">
          <h3 className="text-lg font-medium mb-2">Signature Hash</h3>
          <div className="bg-gray-100 p-3 rounded-md break-all">
            {selectedSignature}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-medium mb-2">Signature Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium">Date:</p>
              <p className="text-sm">2025/02/10 11:06 AM</p>
            </div>
            <div>
              <p className="text-sm font-medium">Status:</p>
              <p className="text-sm text-green-600">Valid</p>
            </div>
            <div>
              <p className="text-sm font-medium">Signer:</p>
              <p className="text-sm">
                {signatureType === "icf" ? "Patient" : "Investigator"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">ICF Version:</p>
              <p className="text-sm">Atopic Eczema V2</p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showModifyModal}
        onClose={() => setShowModifyModal(false)}
        title={
          signatureType === "icf"
            ? "Resend ICF for signature"
            : "signed ICF correction"
        }
        primaryAction={async () => {
          if (signatureType === "signed-icf") {
            setPatientForSignature(selectedPatient);
            setInvestigatorFile(selectedFile);
            await handleUploadCounterSignature();
          } else {
            await handleSendByEmail(selectedPatient);
          }
          setShowModifyModal(false);
        }}
        primaryActionLabel={
          signatureType === "signed-icf" ? "Upload signature" : "Send by email"
        }
        primaryButtonVariant="addbutton"
        secondaryAction={() => {}}
        secondaryActionLabel="Print document"
        tertiaryAction={() => {
          setShowModifyModal(false);
          setSelectedFile(null);
        }}
        tertiaryActionLabel="Cancel"
        size="xl"
      >
        {signatureType === "icf" ? (
          <>
            <p className="mb-4">
              {" "}
              The previously signed Informed Consent Form (ICF) requires
              correction. You have the option to resend it for signature to the
              patient.
            </p>
            <p className="mb-4">
              {" "}
              Without the correctly signed ICF, the patient cannot continue
              participating in the study.
            </p>
            <p className="mb-4">Please choose how you would like to proceed:</p>
          </>
        ) : (
          <>
            {signatureType === "signed-icf" && (
              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Upload corrected signature
                </label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
              </div>
            )}
            <p className="mb-4">
              {" "}
              You need to correct your signature of the Informed Consent Form
              (ICF). You have the option to re-submit the correct version for
              validation.
            </p>
            <p className="mb-4">
              {" "}
              Without a properly signed ICF, the patient's participation in the
              study may be affected.
            </p>
            <p className="mb-4">Please choose how you would like to proceed:</p>
          </>
        )}
      </Modal>

      <Modal
        isOpen={showSignatureRequiredModal}
        onClose={() => setShowSignatureRequiredModal(false)}
        title="Patient consent required"
        primaryAction={() => {
          /* Action for sending email */ setShowSignatureRequiredModal(false);
        }}
        primaryActionLabel="Send by email"
        primaryButtonVariant="addbutton"
        secondaryAction={() => {
          /* Action for printing document */ setShowSignatureRequiredModal(
            false
          );
        }}
        secondaryActionLabel="Print document"
        tertiaryAction={() => setShowSignatureRequiredModal(false)}
        tertiaryActionLabel="Cancel"
        size="xl"
      >
        <p className="mb-4">
          The patient has not yet signed or re-signed the Informed Consent Form.
        </p>
        <p className="mb-4">
          Without this signature, the patient cannot continue participation in
          the study.
        </p>
        <p className="mb-4">Please choose how you would like to proceed:</p>
      </Modal>

      <Modal
        isOpen={showInvestigatorCounterSignatureModal}
        onClose={() => setShowInvestigatorCounterSignatureModal(false)}
        title="Investigator signature"
        primaryAction={handleUploadCounterSignature}
        primaryActionLabel={
          isUploadingCounter ? "Uploading..." : "Counter sign"
        }
        primaryButtonVariant="addbutton"
        secondaryAction={() => {
          const el = document.getElementById("investigator-upload-input");
          if (el) el.click();
        }}
        secondaryActionLabel="Upload signature"
        tertiaryAction={() => setShowInvestigatorCounterSignatureModal(false)}
        tertiaryActionLabel="Cancel"
        size="xl"
      >
        <p className="mb-4">
          {" "}
          As the investigator, I have reviewed and hereby counter-sign the
          informed consent form, confirming the patient's participation under
          the latest consent version.
        </p>
        <p className="mb-4">
          {" "}
          Please
          {" "}
          <button
            type="button"
            onClick={openICFForCounterSign}
            className="text-blue-600 underline inline-flex items-center gap-1"
          >
            <img
              src="/icons/study/Consultation.svg"
              alt="Open ICF"
              className="w-4 h-4"
            />
            open the ICF
          </button>
          {" "}
          to proceed—choose to print and sign manually or validate directly with an electronic signature.
        </p>

        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-sm mb-2">
            <span className="font-medium">Selected file: </span>
            {investigatorFile ? (
              <span className="break-all">
                {investigatorFile.name} (
                {Math.ceil(investigatorFile.size / 1024)} KB)
              </span>
            ) : (
              <span className="italic text-gray-500">No file selected</span>
            )}
          </div>

          <div className="text-xs text-gray-600">
            Site ICF:{" "}
            <span className="font-mono">
              {patientForSignature?.site_icf_id || "N/A"}
            </span>
          </div>

          {isUploadingCounter && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Uploading and recording counter‑signature…
            </div>
          )}
        </div>

        <input
          id="investigator-upload-input"
          type="file"
          className="hidden"
          onChange={handleInvestigatorFileChange}
          accept=".pdf,.jpg,.jpeg,.png"
        />
      </Modal>

      {/* New Modal for Re-consent */}
      <Modal
        isOpen={showReconsentModal}
        onClose={() => setShowReconsentModal(false)}
        title="Request Patient Re-consent"
        primaryAction={handleConfirmReconsent}
        primaryActionLabel="Confirm Request"
        primaryButtonVariant="addbutton"
        secondaryAction={() => setShowReconsentModal(false)}
        secondaryActionLabel="Cancel"
        size="xl"
      >
        <p className="mb-4">
          {" "}
          You are about to request a re-consent from this patient. This will
          update their status to "(Re)consent required" and they will need to
          sign a new Informed Consent Form to continue participating in the
          study.
        </p>
        <p className="mb-4">
          Are you sure you want to proceed with this action?
        </p>
      </Modal>

      <Modal
        isOpen={showContactEditModal}
        onClose={() => setShowContactEditModal(false)}
        title="Patient data modification"
        primaryAction={handleUpdateContact}
        primaryActionLabel="Update"
        primaryButtonVariant="addbutton"
        secondaryAction={() => setShowContactEditModal(false)}
        secondaryActionLabel="Cancel"
        size="max"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="firstName"
              className="block mb-2 text-sm font-medium"
            >
              {" "}
              First name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={editedContactInfo.firstName}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="block mb-2 text-sm font-medium"
            >
              {" "}
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={editedContactInfo.lastName}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="email" className="block mb-2 text-sm font-medium">
              {" "}
              Email address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={editedContactInfo.email}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block mb-2 text-sm font-medium">
              {" "}
              Phone number
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={editedContactInfo.phone}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="col-span-1">
            <label htmlFor="street" className="block mb-2 text-sm font-medium">
              {" "}
              Patient address - Street
            </label>
            <input
              type="text"
              id="street"
              name="street"
              value={editedContactInfo.street}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="col-span-1">
            <label htmlFor="number" className="block mb-2 text-sm font-medium">
              {" "}
              Patient address - Number
            </label>
            <input
              type="text"
              id="number"
              name="number"
              value={editedContactInfo.number}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="country" className="block mb-2 text-sm font-medium">
              {" "}
              Patient address - Country
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={editedContactInfo.country}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="city" className="block mb-2 text-sm font-medium">
              {" "}
              Patient address - City / Town
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={editedContactInfo.city}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="postalCode"
              className="block mb-2 text-sm font-medium"
            >
              {" "}
              Patient address - Postal code
            </label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              value={editedContactInfo.postalCode}
              onChange={handleContactInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
